<template>
    <div>
        <div v-show="responseMessage" class="alert alert-danger">{{ responseMessage }}</div>
        <table class="table">
            <tbody>
            <tr>
                <th style="border-top: 0">{{ $t('Trafikkskole') }}:</th>
                <td style="border-top: 0" class="text-end">{{ officeName }}</td>
            </tr>
            <tr>
                <th>{{ $t('Navn') }}:</th>
                <td class="text-end">{{ studentName }}</td>
            </tr>
            <tr v-if="displaySaldo !== 'none'">
                <th v-if="displaySaldo === 'total'">{{ balanceLabel }} {{ $t('totalt') }}:</th>
                <th v-if="displaySaldo !== 'total'">{{ balanceLabel }} {{ $t('per') }} {{ displaySaldoDate }}:</th>
                <td class="text-end">{{ balanceFormatted }}</td>
            </tr>
            <tr>
                <th style="vertical-align: middle">{{ $t('Beløp å betale') }}:</th>
                <td v-if="amountIsEditable">
                    <input class="form-control text-end" name="amount" type="number" min="0" :max="maxSum" :value="sum" @keypress="validateValue" @change="setSum" @keyup="setSum" />
                </td>
                <td class="text-end" v-if="!amountIsEditable">
                    {{ amountFormatted }}
                    <input type="hidden" name="amount" :value="amount">
                </td>
            </tr>
            <tr v-if="studentPayCardFee">
                <th colspan="2">{{ $t('Straksbetaling') }} <span>{{ transactionFee }}%</span></th>
            </tr>
            <tr v-if="studentPayCardFee">
                <th>{{ $t('Totalsum til {officeName}', {officeName}) }}:</th>
                <td class="text-end"><span id="finalSum">{{sumIncludingFee}}</span> kr</td>
            </tr>
            <tr>
                <th style="vertical-align: middle">{{ $t('Betal fra mobil') }}:</th>
                <td class="text-end">
                    <select class="form-select" name="mobile" @change="setVippsMobile" v-if="mobileNumbers.length">
                        <option v-for="mobileNumber in mobileNumbers" :value="mobileNumber">{{ mobileNumber }}</option>
                    </select>
                    <span v-if="!mobileNumbers.length">{{ $t('Ingen gyldige mobilnummer') }}</span>
                </td>
            </tr>
            </tbody>
        </table>
        <input type="hidden" name="fee" id="fee" :value="transactionFee" />

        <div class="row justify-content-center">
            <div class="col-sm-6 col-12 order-1 order-sm-0 mb-1">
                <a href="/" class="btn btn-secondary btn-block btn-sm-inline cursor-pointer">{{ $t('Gå til {brandingName}', {brandingName: getBrandingName}) }}</a>
            </div>
            <div class="col-sm-6 col-12 order-0 order-sm-1 mb-1 text-end">
                <button @click="send" class="btn btn-primary btn-block btn-sm-inline" :disabled="isLoading || !mobileNumbers.length">
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-show="isLoading"></span>
                    {{ $t('Send Vipps') }}
                </button>
            </div>
        </div>
        <p v-if="infoText" class="text-center" style="margin-top: 25px; color: #777">{{infoText}}</p>
    </div>
</template>

<style scoped>
    select {text-align-last: right;}
    option {direction: rtl;}
</style>

<script setup>
import { ref, computed } from 'vue';
import Api from '../../api/BaseRequest';

const props = defineProps(['paymentData', 'vippsHash']);
const emit = defineEmits(['inprogress', 'error']);

const responseMessage = ref('');
const loading = ref(false);
const sum = ref(props.paymentData.amount > 0 ? props.paymentData.amount : 0);
const maxSum = ref(20000);
const mobile = ref(props.paymentData.mobileNumbers[0]);

const send = () => {
    responseMessage.value = '';
    loading.value = true;

    if (isNaN(sum.value)) {
        responseMessage.value = 'Beløpet er ugyldig.';
        loading.value = false;
        return;
    }
    Api.Post(
        '/v_request/' + props.vippsHash,
        {
            token: CsrfToken.value,
            amount: sum.value,
            mobile: mobile.value,
            v2: true,
        }
    ).then(res => {
        if (res.redirect) {
            window.location = res.message;
            return;
        }
        loading.value = false;
        if (res.data && res.data.message) {
            responseMessage.value = res.data.message;
        }
        if (res.inprogress) {
            emit('inprogress');
        }
        if (res.html) {
            emit('error', res.html);
        }
    });
};

const setSum = (event) => {
    if (parseInt(event.target.value) > maxSum.value) {
        event.target.value = maxSum.value;
    }

    let amount = event.target.value;
    if (isNaN(parseInt(amount))) {
        sum.value = 0;
        return;
    }

    sum.value = Math.round(amount);
};

const setVippsMobile = (event) => {
    mobile.value = event.target.value;
};

const validateValue = (event) => {
    if ([',', '.', '-'].indexOf(event.key) !== -1) {
        event.preventDefault();
    }
};

const officeName = computed(() => props.paymentData.office_name);
const studentName = computed(() => props.paymentData.student_full_name);
const displaySaldo = computed(() => props.paymentData.display_saldo);
const displaySaldoDate = computed(() => props.paymentData.display_saldo_date);
const balanceLabel = computed(() => props.paymentData.balance > 0 ? $t('Skyldig') : $t('Til gode'));
const balanceFormatted = computed(() => props.paymentData.balance_formatted);
const amountFormatted = computed(() => props.paymentData.amount_formatted);
const amountIsEditable = computed(() => props.paymentData.amount_is_editable);
const amount = computed(() => props.paymentData.amount);
const sumIncludingFee = computed(() => {
    if (sum.value <= 0 || transactionFee.value <= 0) {
        return sum.value;
    }
    const fee = Math.max(1, Math.round(sum.value * transactionFee.value / 100));
    return sum.value + fee;
});
const studentPayCardFee = computed(() => props.paymentData.student_pay_card_fee === 1);
const transactionFee = computed(() => props.paymentData.card_fee);
const mobileNumbers = computed(() => props.paymentData.mobileNumbers);
const CsrfToken = computed(() => props.paymentData.csrf_token);
const infoText = computed(() => props.paymentData.infoText);
const isLoading = computed(() => loading.value);
const getBrandingName = computed(() => window.brandingName);
</script>
